import { CohortesPilotage } from './CohortePilotage';
import { ContentManager } from './ContentManager';
import CreativeProjectPanel from './CreativeProjectPanel';
import CRMCorporatePanel from './CRMCorporatePanel';
import CRMCreativePanel from './CRMCreativePanel';
import { CRMPanel } from './CRMPanel';
import { DashboardEngine } from './DashboardEngine';
import { EcoleBranding } from './EcoleBranding';
import { ElevesManager } from './ElevesManager';
import { MessagerieManager } from './MessagerieManager2';
import { ModerationZone } from './ModerationZone';
import { PersonnelManager } from './PersonnelManager';
import { StatsGlobales } from './StatsGlobales';

// ─────────────────────────────────────────────────────────────────────────────
// Registres
// ─────────────────────────────────────────────────────────────────────────────

// Composants génériques — clé = source du module dans le profil
const COSMETIC = {
  dashboard:  DashboardEngine,
  inscriptions:     ElevesManager,
  protocoles:   ContentManager,
  cohortes:   CohortesPilotage,
  personnel:  PersonnelManager,
  branding:   EcoleBranding,
  stats:      StatsGlobales,
  modération: ModerationZone,
  contact:    MessagerieManager,
  crm:        CRMPanel,          // fallback CRM générique
};

// Composants spécialisés — clé = moduleConfig.component
const SPECIALIZED = {
  CRMPanel:               CRMPanel,
  CRMCreativePanel:       CRMCreativePanel,
  CreativeProjectPanel:   CreativeProjectPanel,
  CRMCorporatePanel:      CRMCorporatePanel,
  // Panels CRM par profil — brancher ici quand prêts :
  
  // CRMHealthPanel:      CRMHealthPanel,
  // CRMConsultingPanel:  CRMConsultingPanel,
  CreativePortfolioPanel: () => (
    <div className="flex items-center justify-center h-48 text-zinc-600 text-xs uppercase tracking-widest font-mono border border-zinc-800 bg-black/30">
      Portfolio — à venir
    </div>
  ),
};

// ─────────────────────────────────────────────────────────────────────────────
// Props communes transmises aux composants génériques
// ─────────────────────────────────────────────────────────────────────────────
function commonProps(props) {
  return {
    darkMode:      props.darkMode,
    learners:      props.learners,
    courses:       props.courses,
    cohortes:      props.cohortes,
    staff:         props.staff,
    isEditable:    props.isEditing,
    onSave:        props.handleSaveSection,
    onToggleEdit:  props.toggleEdit,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ModuleRenderer
// ─────────────────────────────────────────────────────────────────────────────
export default function ModuleRenderer(props) {
  const {
    sectionId,
    layout,
    darkMode = true,
    // Métriques dashboard
    totalLearners,
    activeLearners,
    totalPesasCirculating,
    globalCompletionRate,
    criticalAlerts,
    // Mode Lab
    isEditing,
    handleSaveSection,
    toggleEdit,
  } = props;

  // Config du module dans le layout du profil actif
  const moduleConfig = layout?.modules?.[sectionId];
  const source       = moduleConfig?.source ?? sectionId;
  const renderMode   = moduleConfig?.renderMode ?? 'cosmetic';
  const component    = moduleConfig?.component ?? null;

  // ── 1. SPECIALIZED — composant dédié défini dans le layout ──────────────
  if (renderMode === 'specialized' && component) {
    const SpecializedComponent = SPECIALIZED[component];
    if (SpecializedComponent) {
      return <SpecializedComponent darkMode={darkMode} />;
    }
    return (
      <div className="text-zinc-500 text-xs font-mono p-4 border border-zinc-800 bg-black/20">
        Composant spécialisé non trouvé : <code>{component}</code>
      </div>
    );
  }

  // ── 2. COSMETIC / CONFIGURABLE — composant générique par source ──────────
  // Pour les sources connues on passe les props spécifiques,
  // sinon on passe les props communes en masse.

  switch (source) {

    case 'dashboard':
      return (
        <DashboardEngine
          darkMode={darkMode}
          stats={{
            totalLearners,
            activeLearners,
            totalPesas:       totalPesasCirculating,
            globalCompletion: globalCompletionRate,
            criticalAlerts,
          }}
        />
      );

    case 'contact':
      return <MessagerieManager darkMode={darkMode} />;

    case 'protocoles':
      return <ContentManager darkMode={darkMode} />;

    case 'inscriptions':
      return (
        <ElevesManager
          darkMode={darkMode}
          learners={props.learners}
          isEditable={isEditing}
          onSave={handleSaveSection}
          onToggleEdit={toggleEdit}
        />
      );

    case 'cohortes':
      return (
        <CohortesPilotage
          darkMode={darkMode}
          cohortes={props.cohortes}
          isEditable={isEditing}
          onSave={handleSaveSection}
          onToggleEdit={toggleEdit}
        />
      );

    case 'personnel':
      return (
        <PersonnelManager
          darkMode={darkMode}
          staff={props.staff}
          isEditable={isEditing}
          onSave={handleSaveSection}
          onToggleEdit={toggleEdit}
        />
      );

    case 'branding':
      return (
        <EcoleBranding
          darkMode={darkMode}
          isEditable={isEditing}
          onSave={handleSaveSection}
        />
      );

    case 'stats':
      return <StatsGlobales darkMode={darkMode} />;

    case 'modération':
      return <ModerationZone darkMode={darkMode} />;

    // ── CRM : dispatch par profil ──────────────────────────────────────────
    // Le renderMode 'specialized' + component gère déjà les profils
    // qui ont un panel CRM dédié (creative → CRMCreativePanel etc.).
    // Ce cas ne traite que le fallback cosmetic (profils sans component défini).
    case 'crm':
      return <CRMPanel darkMode={darkMode} />;

    // ── Sources inconnues (modules futurs, portfolio, etc.) ────────────────
    default: {
      const Fallback = COSMETIC[source];
      if (Fallback) {
        return <Fallback {...commonProps(props)} />;
      }
      return (
        <div className="flex items-center justify-center h-48 text-zinc-600 text-xs uppercase tracking-widest font-mono border border-zinc-800 bg-black/20">
          Module <code className="mx-1 text-zinc-500">{sectionId}</code> — source inconnue : <code className="ml-1 text-zinc-500">{source}</code>
        </div>
      );
    }
  }
}